import { useState, useEffect } from "react";
import { Layout } from "./components/Layout";
import { Modal } from "shared/components";
import { useMakeDecisionController } from "./controller";
import { Views } from "./types";
import { MakeDecisionView } from "./views/MakeDecisionView";
import { DecisionSettingsView } from "./views/DecisionSettingsView";
import { ConditionsView } from "./views/ConditionsView";
import { EmailView } from "./views/EmailView";
import api from "api/landlord/address";
import { SuccessView } from "./views/SuccessView";
import { getErrorMessage } from "utils/error";
import { toast } from "react-toastify";
import { Application, DecisionAction } from "shared/types";
import { notesQueryKey } from "shared/queries/useNotes";
import { useQueryClient } from "@tanstack/react-query";
import { Loader } from "shared/components/Loader";
import { useAuthContext } from "providers/Auth";
import { Template } from "types/landlord/verificationTemplate";
import { Applicant } from "types/application";

export const MakeDecisionModal = ({
  opened,
  onClose,
  applicationId
}: {
  opened: boolean;
  onClose: () => void;
  applicationId: string;
}) => {
  const { type } = useAuthContext();
  const queryClient = useQueryClient();
  const {
    applicationMeta,
    formData,
    view,
    setNextView,
    setPrevView,
    flow,
    setFormData,
    resetConditions
  } = useMakeDecisionController(applicationId);

  const [successViewFormData, setSuccessViewFormData] = useState(formData);
  const [showDetailedConditionsView, setShowDetailedConditionsView] =
    useState(true);

  useEffect(() => {
    if (showDetailedConditionsView)
      resetConditions(
        applicationMeta.data?.rule_reasons,
        applicationMeta.data?.recommendation
      );
  }, [showDetailedConditionsView]);

  const onSubmit = async () => {
    const conditions = formData.conditions;
    if (formData.additionalCondition)
      conditions.push(formData.additionalCondition);
    const body = {
      send_applicants_to_pms: type.isPMS
        ? formData.addApplicantsToPMS
        : undefined,
      note_message: formData.decisionNotes,
      email_message: formData.additionalEmailText,
      skip_sending_email: !formData.sendNotifications
    };

    (formData.decision === DecisionAction.Approve ||
    formData.decision === DecisionAction.ConditionalApprove
      ? api.approveApplication(applicationId, {
          ...body,
          conditions:
            formData.decision === DecisionAction.Approve
              ? undefined
              : conditions
        })
      : api.rejectApplication(applicationId, {
          ...body,
          reasons: conditions
        })
    )
      .then(() => {
        const unitMagicLink = applicationMeta.data?.unit.magic_link;

        if (formData.unlistUnit && unitMagicLink?.active && unitMagicLink.id)
          return api.updateMagicLink(unitMagicLink.id, { active: false });
        else return Promise.resolve();
      })
      .then(() => {
        queryClient.invalidateQueries({
          queryKey: notesQueryKey(applicationId)
        });
        queryClient.invalidateQueries({
          queryKey: ["APPLICATION", applicationId]
        });

        if (formData.decision === DecisionAction.Decline) onClose();
        else {
          setSuccessViewFormData(formData);
          setNextView();
        }
      })
      .catch((err) => {
        toast.error(getErrorMessage(err));
      });
  };

  const getFullAddress = (
    address:
      | (Application & {
          verification_template: Template;
          score: number;
        })
      | undefined
  ) => {
    let fullAddress: string = "";
    if (!address) return fullAddress;
    if (address.unit.title) fullAddress += `${address.unit.title}, `;
    if (address.property?.address.address1)
      fullAddress += `${address.property.address.address1}, `;
    if (address.property?.address.city)
      fullAddress += `${address.property.address.city} `;
    if (address.property?.address.state_or_province)
      fullAddress += `${address.property.address.state_or_province}, `;
    if (address.property?.address.postal_code)
      fullAddress += `${address.property.address.postal_code}`;

    return fullAddress || "--";
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      panelClassName="max-h-[840px] h-full max-w-[880px] w-full animate-appear border border-alpha-100 shadow-lg"
    >
      <Layout
        view={view}
        onClose={onClose}
        onNext={setNextView}
        onPrev={setPrevView}
        formData={formData}
        onSubmit={onSubmit}
        showDetailedConditionsView={showDetailedConditionsView}
        setShowDetailedConditionsView={setShowDetailedConditionsView}
        recommendation={applicationMeta.data?.recommendation}
        recommendationMethod={
          applicationMeta.data?.verification_template.recommendation_method
        }
      >
        {applicationMeta.isLoading ? (
          <div className="h-full w-full flex items-center justify-center">
            <Loader />
          </div>
        ) : view === Views.MakeDecision ? (
          <MakeDecisionView
            onClose={onClose}
            applicationId={applicationMeta.data?.id}
            requiresManualExecution={
              applicationMeta.data?.requires_manual_or_conditional_execution
            }
            recommendationMethod={
              applicationMeta.data?.verification_template.recommendation_method
            }
            applicants={applicationMeta.data?.applicants || []}
            recommendation={applicationMeta.data?.recommendation}
            templateCriteriaSettings={{
              conditional_approval_range_end: Number(
                applicationMeta.data?.verification_template.screening_criteria
                  ?.conditional_approval_range_end
              ),
              decline_range_end: Number(
                applicationMeta.data?.verification_template.screening_criteria
                  ?.decline_range_end
              )
            }}
            sendApplicantsToPMS={{
              [DecisionAction.Approve]: Boolean(
                applicationMeta.data?.verification_template
                  .add_applicants_to_pms_on_approve
              ),
              [DecisionAction.ConditionalApprove]: Boolean(
                applicationMeta.data?.verification_template
                  .add_applicants_to_pms_on_conditionally_approve
              ),
              [DecisionAction.Decline]: Boolean(
                applicationMeta.data?.verification_template
                  .add_applicants_to_pms_on_decline
              )
            }}
            formData={formData}
            score={Number(applicationMeta.data?.score)}
            view={view}
            flow={flow}
            setFormData={setFormData}
          />
        ) : view === Views.DecisionSettings ? (
          <DecisionSettingsView
            formData={formData}
            setFormData={setFormData}
            view={view}
            flow={flow}
            address={getFullAddress(applicationMeta.data)}
            disableAddApplicantsToPms={!applicationMeta.data?.property?.pms_id}
          />
        ) : view === Views.Conditions ? (
          <ConditionsView
            formData={formData}
            setFormData={setFormData}
            view={view}
            flow={flow}
            recommendation={applicationMeta.data?.recommendation}
            showDetailedConditionsView={showDetailedConditionsView}
            setShowDetailedConditionsView={setShowDetailedConditionsView}
            ruleReasons={applicationMeta.data?.rule_reasons}
            recommendationMethod={
              applicationMeta.data?.verification_template.recommendation_method
            }
          />
        ) : view === Views.Email ? (
          <EmailView
            applicants={applicationMeta.data?.applicants || []}
            address={getFullAddress(applicationMeta.data)}
            formData={formData}
            setFormData={setFormData}
            view={view}
            flow={flow}
          />
        ) : // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- LET'S KEEP IT CLEAR AND DEFINED FOR NOW
        view === Views.Success ? (
          <SuccessView
            formData={successViewFormData}
            address={getFullAddress(applicationMeta.data)}
            applicants={
              (applicationMeta.data?.applicants as unknown[] | undefined as
                | Applicant[]
                | undefined) || []
            }
          />
        ) : null}
      </Layout>
    </Modal>
  );
};
